import { useState, useRef, useEffect, useMemo } from "react";
import {
    Plus, Tags, Search, X,
    Trash2, Pencil, Layers, ArrowRight, Check, Sparkles, Copy, Lock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, verticalListSortingStrategy, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { toast } from "sonner";
import { useSizing } from "@/lib/sizing-store";
import { PageHeader, EmptyState, PrimaryButton, SecondaryButton, SortableRow } from "@/components/design-system";
import {
    TemplateRenameDialog, AddItemDialog, BlockersDialog, CloneDialog,
} from "@/components/sizing/TemplateActionDialogs";
import { InlineTitleEdit } from "@/components/sizing/InlineTitleEdit";

const SAMPLE_HINTS = [
    { name: "Alfa (XS–XXL)", sample: ["XS", "S", "M", "L", "XL", "XXL"] },
    { name: "Avrupa Numara", sample: ["32", "34", "36", "38", "40", "42"] },
    { name: "Jean (24–36)", sample: ["24", "26", "28", "30", "32", "34", "36"] },
];

export default function Sizes() {
    const {
        series, tables, addSeries, updateSeries, removeSeries,
        renameSizeInSeries, addSizeToSeries, deleteSizeFromSeries, cloneSeries,
    } = useSizing();
    const [activeId, setActiveId] = useState(null);
    const [dialogState, setDialogState] = useState(null); // { mode: 'create'|'edit', initial? }
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [query, setQuery] = useState("");
    // Rename flow — templates are the SINGLE source of truth now, so this
    // modal is single-action ("N tabloda güncelle" / "Vazgeç"). No more
    // fork-and-strand mode. See PRD UX friction doc.
    const [renameState, setRenameState] = useState(null);
    // Add-size flow — opt-in per-table picker.
    const [addSizeState, setAddSizeState] = useState(null);
    // Blockers dialog — shown on 409 from delete-series or delete-size.
    const [blockers, setBlockers] = useState(null);
    // Clone dialog — creates a series fork with a new name.
    const [cloneState, setCloneState] = useState(null);

    // Compute usage count per series — how many tables were sourced from it.
    // Matches by explicit sourceSeriesId with legacy fallback on seriesLabel.
    const usageBySeriesId = useMemo(() => {
        const map = new Map();
        (tables || []).forEach((t) => {
            let sid = t.sourceSeriesId;
            if (!sid) {
                const match = series.find((s) => s.name === t.seriesLabel);
                if (match) sid = match.id;
            }
            if (sid) map.set(sid, (map.get(sid) || 0) + 1);
        });
        return map;
    }, [tables, series]);
    // Tables that reference this specific size within a given series — used
    // by the rename propagation modal to list the blast radius.
    const tablesUsingSize = (seriesId, size) => {
        const s = series.find((x) => x.id === seriesId);
        return (tables || []).filter((t) => {
            const linked = t.sourceSeriesId === seriesId
                || (!t.sourceSeriesId && s && t.seriesLabel === s.name);
            return linked && (t.sizes || []).includes(size);
        });
    };
    // All tables sourced from a given series — regardless of which sizes
    // they contain. Used by the add-size modal for the "hangi tablolara?"
    // picker.
    const tablesUsingSeries = (seriesId) => {
        const s = series.find((x) => x.id === seriesId);
        return (tables || []).filter((t) => (
            t.sourceSeriesId === seriesId
            || (!t.sourceSeriesId && s && t.seriesLabel === s.name)
        ));
    };

    useEffect(() => {
        if (series.length && !series.find((s) => s.id === activeId)) {
            setActiveId(series[0].id);
        }
        if (!series.length) setActiveId(null);
    }, [series, activeId]);

    const filtered = series.filter((s) =>
        !query.trim() ||
        s.name.toLowerCase().includes(query.toLowerCase())
    );
    const current = series.find((s) => s.id === activeId) || null;

    const handleReorderSizes = (nextSizes) => {
        if (!current) return;
        // Reorder ONLY — same set, different order. Backend permits this on
        // PUT /series/{sid}; add/delete go through their own endpoints.
        updateSeries(current.id, { sizes: nextSizes });
    };

    // "Sil" on a size chip. Empty rows → toast + apply immediately; blocked
    // by non-empty values → BlockersDialog with the offending tables.
    const handleRemoveSize = async (idx) => {
        if (!current) return;
        const removed = current.sizes[idx];
        try {
            const res = await deleteSizeFromSeries(current.id, removed);
            toast.success(
                (res?.tablesUpdated || 0) > 0
                    ? `${removed} bedeni ${res.tablesUpdated} tablodan da temizlendi`
                    : `${removed} bedeni "${current.name}" serisinden çıkarıldı`
            );
        } catch (e) {
            if (e?.response?.status === 409) {
                const payload = e.response.data?.detail;
                setBlockers({
                    title: `"${removed}" bedeni silinemez`,
                    description: typeof payload === "object" ? payload?.detail : payload,
                    blockers: (typeof payload === "object" ? payload?.blockers : []) || [],
                    forkLabel: "Bu seriyi kopyala",
                    forkKind: "series",
                    forkId: current.id,
                });
                return;
            }
            toast.error(e?.response?.data?.detail || `${removed} silinemedi`);
        }
    };

    // Add-size inline entry point. Opens the AddItemDialog with pre-filled
    // draft — the picker handles the "all/none/some" tables scope.
    const handleAddSizeInline = (val) => {
        if (!current || !val.trim()) return;
        if (current.sizes.includes(val.trim())) {
            toast.error("Bu beden zaten ekli");
            return;
        }
        setAddSizeState({ defaultName: val.trim() });
    };

    return (
        <TooltipProvider delayDuration={200}>
            <div className="space-y-6" data-testid="sizes-page">
                <PageHeader
                    icon={Tags}
                    title="Beden Serileri"
                    description="Mağazandaki ürünlerin paylaşacağı beden setlerini tanımla. Bir kez oluştur, beden tablosu kurarken seç — sadece gerek duyduğun bedenleri işaretle."
                    actions={
                        <PrimaryButton
                            onClick={() => setDialogState({ mode: "create" })}
                            data-testid="size-system-create-btn"
                        >
                            <Plus className="h-3.5 w-3.5" /> Yeni Seri
                        </PrimaryButton>
                    }
                />

                {series.length === 0 ? (
                    <SeriesEmptyState
                        onCreate={() => setDialogState({ mode: "create" })}
                        onUseSample={(s) => setDialogState({ mode: "create", initial: { name: s.name, sizes: s.sample } })}
                    />
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-6">
                        <div className="rounded-xl border border-border bg-surface overflow-hidden self-start">
                            <div className="px-3 py-2.5 border-b border-border">
                                <div className="relative">
                                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                                    <Input
                                        placeholder="Seri ara…"
                                        className="h-8 pl-8 text-xs bg-background"
                                        value={query}
                                        onChange={(e) => setQuery(e.target.value)}
                                        data-testid="series-search"
                                    />
                                </div>
                            </div>
                            <div className="divide-y divide-border max-h-[560px] overflow-y-auto scroll-thin">
                                {filtered.map((s) => {
                                    const usage = usageBySeriesId.get(s.id) || 0;
                                    return (
                                    <button
                                        key={s.id}
                                        onClick={() => setActiveId(s.id)}
                                        className={`w-full text-left px-4 py-3 transition-colors ${activeId === s.id ? "bg-muted/60" : "hover:bg-muted/30"}`}
                                        data-testid={`size-system-${s.id}`}
                                    >
                                        <div className="flex items-center justify-between gap-2">
                                            <div className="font-display text-sm font-semibold tracking-tight truncate">{s.name}</div>
                                            <Badge variant="outline" className="text-[10px] font-mono tracking-wider shrink-0">{s.sizes.length}</Badge>
                                        </div>
                                        <div className="mt-2 flex flex-wrap gap-1">
                                            {s.sizes.slice(0, 6).map((sz) => (
                                                <span key={sz} className="text-[10px] font-mono text-muted-foreground inline-flex items-center justify-center rounded border border-foreground/15 bg-surface px-1.5 py-0.5">
                                                    {sz}
                                                </span>
                                            ))}
                                            {s.sizes.length > 6 && (
                                                <span className="text-[10px] text-muted-foreground/60 self-center">+{s.sizes.length - 6}</span>
                                            )}
                                        </div>
                                        {usage > 0 && (
                                            <div className="mt-2 text-[10px] text-muted-foreground inline-flex items-center gap-1">
                                                <span className="h-1 w-1 rounded-full bg-brand" aria-hidden="true" /> {usage} tabloda kullanılıyor
                                            </div>
                                        )}
                                    </button>
                                    );
                                })}
                                {filtered.length === 0 && (
                                    <div className="px-4 py-8 text-center text-xs text-muted-foreground">
                                        &ldquo;{query}&rdquo; için sonuç yok
                                    </div>
                                )}
                            </div>
                        </div>

                        {current && (
                            <SeriesDetail
                                current={current}
                                usage={usageBySeriesId.get(current.id) || 0}
                                tables={tables}
                                onRename={async (newName) => {
                                    // Inline title rename — persisted via PUT.
                                    // Only the series name changes; sizes list
                                    // is untouched so the permutation-guard on
                                    // the backend accepts the patch.
                                    await updateSeries(current.id, { name: newName });
                                    toast.success(`Seri adı "${newName}" olarak güncellendi`);
                                }}
                                onDelete={() => {
                                    // If the series is used, skip the pointless
                                    // "Evet, Sil" step (which just fails with 409)
                                    // and jump straight to the blockers list —
                                    // same UX as deleting a size inside a series.
                                    const usage = usageBySeriesId.get(current.id) || 0;
                                    if (usage === 0) {
                                        setConfirmDelete(current);
                                        return;
                                    }
                                    const linked = tablesUsingSeries(current.id);
                                    setBlockers({
                                        title: `"${current.name}" silinemez`,
                                        description: "Bu seri tabloda kullanılıyor — önce bu tabloları sil ya da seriyi kopyalayarak yeni bir sürüm oluştur.",
                                        blockers: linked.map((t) => ({ id: t.id, name: t.name })),
                                        forkLabel: "Bu seriyi kopyala",
                                        forkKind: "series",
                                        forkId: current.id,
                                    });
                                }}
                                onClone={() => setCloneState({
                                    kind: "series",
                                    id: current.id,
                                    title: `${current.name} — kopyala`,
                                    description: "Bu bedenlerle yeni bir seri oluşturulur. Mevcut tablolar etkilenmez.",
                                    defaultName: `${current.name} (kopya)`,
                                })}
                                onReorderSizes={handleReorderSizes}
                                onRemoveSize={handleRemoveSize}
                                onAddSize={handleAddSizeInline}
                                onRenameSize={(oldName, newName) => {
                                    const affected = tablesUsingSize(current.id, oldName);
                                    setRenameState({
                                        entityKind: "series",
                                        entityId: current.id,
                                        entityName: current.name,
                                        oldName, newName, affected,
                                    });
                                }}
                            />
                        )}
                    </div>
                )}
            </div>

            <SeriesDialog
                key={dialogState?.mode + (dialogState?.initial?.id || "new")}
                state={dialogState}
                tables={tables}
                onClose={() => setDialogState(null)}
                onRequestPropagate={(seriesId, seriesName, oldName, newName) => {
                    const affected = tablesUsingSize(seriesId, oldName);
                    setRenameState({
                        entityKind: "series",
                        entityId: seriesId,
                        entityName: seriesName,
                        oldName, newName, affected,
                    });
                }}
                onSubmit={(values) => {
                    if (dialogState.mode === "edit") {
                        // In edit mode, only allow name + reorder via PUT. Add/
                        // delete are handled by the inline panel; rename is
                        // handled by the TemplateRenameDialog which already
                        // hits the server. So we send `sizes` ONLY when it's a
                        // permutation of the original (server would 400 otherwise).
                        const orig = dialogState.initial?.sizes || [];
                        const origSet = new Set(orig);
                        const nextSet = new Set(values.sizes || []);
                        const permutation = orig.length === values.sizes.length &&
                            [...origSet].every((s) => nextSet.has(s));
                        const patch = { name: values.name };
                        if (permutation) patch.sizes = values.sizes;
                        updateSeries(dialogState.initial.id, patch);
                        toast.success(`${values.name} güncellendi`);
                    } else {
                        const created = addSeries(values);
                        setActiveId(created.id);
                        toast.success(`${values.name} oluşturuldu`);
                    }
                    setDialogState(null);
                }}
            />

            {/* Delete-series confirmation. Only reached when the series is
                UNUSED — used series jump straight to BlockersDialog (see the
                onDelete handler above) so the merchant never sees a misleading
                "Evet, Sil" button that would just fail. */}
            <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{confirmDelete?.name} silinsin mi?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Bu seriyi hiçbir tablo kullanmıyor — güvenle silinebilir.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="delete-cancel">Vazgeç</AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="delete-confirm"
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            onClick={async (e) => {
                                e.preventDefault();
                                if (!confirmDelete) return;
                                const target = confirmDelete;
                                try {
                                    await removeSeries(target.id);
                                    toast.success(`${target.name} silindi`);
                                    if (activeId === target.id) setActiveId(null);
                                } catch (err) {
                                    // Server-side race guard: if a table was
                                    // linked between the check and the delete,
                                    // fall through to BlockersDialog here too.
                                    if (err?.response?.status === 409) {
                                        const payload = err.response.data?.detail;
                                        setBlockers({
                                            title: `"${target.name}" silinemez`,
                                            description: typeof payload === "object" ? payload?.detail : payload,
                                            blockers: (typeof payload === "object" ? payload?.tables : []) || [],
                                            forkLabel: "Bu seriyi kopyala",
                                            forkKind: "series",
                                            forkId: target.id,
                                        });
                                    } else {
                                        toast.error("Silme başarısız");
                                    }
                                }
                                setConfirmDelete(null);
                            }}
                        >
                            Evet, Sil
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* New single-action rename dialog. No more "sadece burada güncelle"
                fork — templates are the single source of truth (see PRD UX
                friction fix). Offers a shortcut to clone the series if the
                merchant needs a variant. */}
            <TemplateRenameDialog
                state={renameState}
                onClose={() => setRenameState(null)}
                onSubmit={async () => {
                    if (!renameState) return;
                    try {
                        const res = await renameSizeInSeries(
                            renameState.entityId,
                            renameState.oldName,
                            renameState.newName,
                        );
                        toast.success(
                            (res?.tablesUpdated || 0) > 0
                                ? `"${renameState.oldName}" → "${renameState.newName}" · ${res.tablesUpdated} tabloda güncellendi`
                                : `"${renameState.oldName}" → "${renameState.newName}" olarak yeniden adlandırıldı`
                        );
                    } catch (_) { /* store already toasted */ }
                    finally { setRenameState(null); }
                }}
                onFork={() => {
                    if (!renameState) return;
                    const s = series.find((x) => x.id === renameState.entityId);
                    setCloneState({
                        kind: "series",
                        id: renameState.entityId,
                        title: `${s?.name || "Seri"} — kopyala`,
                        description: "Aynı bedenlerle yeni bir seri oluşturulur. Mevcut tablolar etkilenmez.",
                        defaultName: `${s?.name || "Seri"} (kopya)`,
                    });
                    setRenameState(null);
                }}
            />

            {/* Add-size dialog with opt-in per-table picker. */}
            <AddItemDialog
                open={!!addSizeState}
                onClose={() => setAddSizeState(null)}
                itemKind="size"
                defaultName={addSizeState?.defaultName || ""}
                linkedTables={current ? tablesUsingSeries(current.id).map((t) => ({ id: t.id, name: t.name })) : []}
                existingNames={current?.sizes || []}
                onSubmit={async ({ name, addToTables }) => {
                    if (!current) return;
                    try {
                        const res = await addSizeToSeries(current.id, name, addToTables);
                        const n = res?.tablesUpdated || 0;
                        toast.success(
                            n > 0
                                ? `${name} bedeni eklendi · ${n} tabloya da eklendi`
                                : `${name} bedeni ${current.name} serisine eklendi`
                        );
                        setAddSizeState(null);
                    } catch (_) { /* store toasted */ }
                }}
            />

            {/* 409 blockers list. Offers a Kopyala shortcut. */}
            <BlockersDialog
                state={blockers}
                onClose={() => setBlockers(null)}
                onFork={blockers?.forkKind === "series" ? () => {
                    const s = series.find((x) => x.id === blockers.forkId);
                    setCloneState({
                        kind: "series",
                        id: blockers.forkId,
                        title: `${s?.name || "Seri"} — kopyala`,
                        description: "Aynı bedenlerle yeni bir seri oluşturulur. Mevcut tablolar etkilenmez.",
                        defaultName: `${s?.name || "Seri"} (kopya)`,
                    });
                    setBlockers(null);
                } : undefined}
            />

            {/* Clone dialog — creates a series fork. */}
            <CloneDialog
                state={cloneState}
                onClose={() => setCloneState(null)}
                onSubmit={async (newName) => {
                    if (!cloneState) return;
                    try {
                        const cloned = await cloneSeries(cloneState.id, newName);
                        toast.success(`${cloned.name} oluşturuldu`);
                        if (cloned?.id) setActiveId(cloned.id);
                    } catch (_) { /* store toasted */ }
                    setCloneState(null);
                }}
            />
        </TooltipProvider>
    );
}

/**
 * NOTE: The legacy `RenamePropagateDialog` was removed in the "single source
 * of truth" refactor. Rename is now global (templates own the name, tables
 * always follow). See `TemplateRenameDialog` in
 * `@/components/sizing/TemplateActionDialogs` for the replacement.
 */

// ---------- Empty state (uses design-system EmptyState with sample templates in secondary slot) ----------
const SeriesEmptyState = ({ onCreate, onUseSample }) => (
    <EmptyState
        icon={Layers}
        title="Henüz beden serisi yok"
        description={"Beden serileri, mağazanın kullandığı ortak setlerdir — örneğin \u201cAlfa XS–XXL\u201d veya \u201cAvrupa 32–46\u201d. Bir kere tanımlarsın; ürünlere tablo eklerken bu setten istediğin bedenleri tek tıkla seçersin."}
        action={{
            label: "İlk Seriyi Oluştur",
            onClick: onCreate,
            icon: Plus,
            testId: "empty-create-series",
        }}
        testId="series-empty-state"
        secondary={
            <>
                <div className="text-[10px] font-semibold tracking-wider uppercase text-muted-foreground mb-3 inline-flex items-center gap-1.5">
                    <Sparkles className="h-3 w-3" strokeWidth={2} /> Hızlı Başlangıç Şablonları
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
                    {SAMPLE_HINTS.map((s) => (
                        <button
                            key={s.name}
                            onClick={() => onUseSample(s)}
                            className="text-left rounded-lg border border-border bg-surface p-3.5 hover:border-foreground/30 transition-colors group"
                            data-testid={`sample-${s.name}`}
                        >
                            <div className="flex items-center justify-between">
                                <div className="font-display text-sm font-semibold tracking-tight">{s.name}</div>
                                <ArrowRight className="h-3.5 w-3.5 text-muted-foreground group-hover:text-foreground transition-colors" />
                            </div>
                            <div className="mt-2 flex flex-wrap gap-1">
                                {s.sample.map((x) => (
                                    <span key={x} className="text-[10px] font-mono inline-flex items-center justify-center rounded border border-foreground/15 bg-surface px-1.5 py-0.5">{x}</span>
                                ))}
                            </div>
                        </button>
                    ))}
                </div>
            </>
        }
    />
);

// ---------- Detail panel ----------
const SeriesDetail = ({ current, usage = 0, tables = [], onRename, onDelete, onClone, onReorderSizes, onRemoveSize, onAddSize, onRenameSize }) => {
    const [inlineSize, setInlineSize] = useState("");
    // Inline rename state: which size chip is currently being renamed and its
    // draft value. Only one chip can be in rename-mode at a time.
    const [renameIdx, setRenameIdx] = useState(-1);
    const [renameDraft, setRenameDraft] = useState("");
    const inputRef = useRef(null);
    // Per-size usage counts — how many tables sourced from this series
    // currently have this exact size in their snapshot. Used to render the
    // lock UX + N-tabloda badge.
    const usageBySize = useMemo(() => {
        const m = new Map();
        (tables || []).forEach((t) => {
            const linked = t.sourceSeriesId === current?.id
                || (!t.sourceSeriesId && current && t.seriesLabel === current.name);
            if (!linked) return;
            (t.sizes || []).forEach((s) => m.set(s, (m.get(s) || 0) + 1));
        });
        return m;
    }, [tables, current]);
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const handleDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        const oldIndex = current.sizes.indexOf(active.id);
        const newIndex = current.sizes.indexOf(over.id);
        if (oldIndex < 0 || newIndex < 0) return;
        onReorderSizes(arrayMove(current.sizes, oldIndex, newIndex));
    };

    return (
        <div className="rounded-xl border border-border bg-surface" data-testid="series-detail">
            <div className="px-5 pt-5 pb-3 border-b border-border flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="font-display text-base font-semibold tracking-tight">
                        <InlineTitleEdit
                            value={current.name}
                            onSave={onRename}
                            testId="series-title-edit"
                        />
                    </div>
                    {usage > 0 && (
                        <div className="mt-1 text-[10.5px] text-muted-foreground inline-flex items-center gap-1.5" data-testid="series-usage-chip">
                            <span className="h-1.5 w-1.5 rounded-full bg-brand" aria-hidden="true" />
                            {usage} tabloda kullanılıyor · <span className="text-foreground/70">tüm tablolar bu seriyi takip eder</span>
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                    <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5" onClick={onClone} data-testid="series-clone-btn">
                        <Copy className="h-3 w-3" /> Kopyala
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={onDelete} data-testid="series-delete-btn">
                        <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                </div>
            </div>
            <div className="p-5">
                <div className="text-[11px] font-semibold tracking-wider uppercase text-muted-foreground mb-3">
                    Bedenler · sıralama ({current.sizes.length})
                </div>
                {current.sizes.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-border px-5 py-8 text-center">
                        <p className="text-xs text-muted-foreground">Bu seride henüz beden yok. Aşağıdan ekle.</p>
                    </div>
                ) : (
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                        <SortableContext items={current.sizes} strategy={verticalListSortingStrategy}>
                            <div className="rounded-lg border border-border overflow-hidden" data-testid="sizes-list">
                                {current.sizes.map((sz, i) => (
                                    <SortableRow
                                        key={sz}
                                        id={sz}
                                        index={i}
                                        label={sz}
                                        usageCount={usageBySize.get(sz) || 0}
                                        onRemove={() => onRemoveSize(i)}
                                        onRename={onRenameSize}
                                        testIdPrefix="size"
                                        labelClassName="font-display font-bold text-sm"
                                    />
                                ))}
                            </div>
                        </SortableContext>
                    </DndContext>
                )}
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        onAddSize(inlineSize);
                        setInlineSize("");
                        setTimeout(() => inputRef.current?.focus(), 0);
                    }}
                    className="mt-3 flex items-center gap-2"
                >
                    <Input
                        ref={inputRef}
                        value={inlineSize}
                        onChange={(e) => setInlineSize(e.target.value)}
                        placeholder="örn. XXXL, 48, 11y"
                        className="h-8 text-xs bg-background flex-1 max-w-[220px]"
                        data-testid="inline-size-input"
                    />
                    <Button type="submit" size="sm" variant="outline" className="h-8 text-xs gap-1.5" disabled={!inlineSize.trim()} data-testid="inline-size-add">
                        <Plus className="h-3 w-3" /> Ekle
                    </Button>
                </form>
            </div>
        </div>
    );
};

// ---------- Create / Edit Dialog ----------
const SeriesDialog = ({ state, onClose, onSubmit, tables = [], onRequestPropagate }) => {
    const isEdit = state?.mode === "edit";
    const [name, setName] = useState(state?.initial?.name || "");
    const [sizes, setSizes] = useState(state?.initial?.sizes || []);
    const [sizeInput, setSizeInput] = useState("");
    const sizeRef = useRef(null);
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    // Downstream usage per size — used to render the "N tabloda" chip inside
    // the edit dialog. Recomputes when the parent's `tables` array changes
    // (e.g. after a rename propagates and the store refreshes).
    const usageBySize = useMemo(() => {
        const m = new Map();
        if (!isEdit || !state?.initial?.id) return m;
        const seriesId = state.initial.id;
        const seriesName = state.initial.name;
        (tables || []).forEach((t) => {
            const linked = t.sourceSeriesId === seriesId
                || (!t.sourceSeriesId && t.seriesLabel === seriesName);
            if (!linked) return;
            (t.sizes || []).forEach((s) => m.set(s, (m.get(s) || 0) + 1));
        });
        return m;
    }, [tables, state, isEdit]);

    useEffect(() => {
        if (state) {
            setName(state.initial?.name || "");
            setSizes(state.initial?.sizes || []);
            setSizeInput("");
        }
    }, [state]);

    if (!state) return null;

    const addSize = () => {
        const v = sizeInput.trim();
        if (!v) return;
        if (sizes.includes(v)) {
            toast.error("Bu beden zaten ekli");
            return;
        }
        setSizes((arr) => [...arr, v]);
        setSizeInput("");
        sizeRef.current?.focus();
    };

    const removeAt = (i) => setSizes((arr) => arr.filter((_, idx) => idx !== i));
    // Inline rename inside the edit dialog. Local mutation for immediate UI
    // feedback + if the size is used in downstream tables (usageCount > 0),
    // ALSO delegates to the parent so it can open the RenamePropagateDialog
    // and let the merchant choose whether to sync the name across tables.
    const renameAt = (oldName, newName) => {
        if (!newName || newName === oldName) return;
        if (sizes.includes(newName)) {
            toast.error(`"${newName}" bu seride zaten var`);
            return;
        }
        setSizes((arr) => arr.map((s) => (s === oldName ? newName : s)));
        // Only trigger the propagation flow if this size is currently used in
        // at least one downstream table AND the parent supplied a handler.
        // Local-only edits (empty series or unused sizes) stay quiet.
        if (isEdit && state?.initial?.id && (usageBySize.get(oldName) || 0) > 0) {
            onRequestPropagate && onRequestPropagate(
                state.initial.id,
                state.initial.name,
                oldName,
                newName,
            );
        }
    };
    const handleDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        const oldIndex = sizes.indexOf(active.id);
        const newIndex = sizes.indexOf(over.id);
        if (oldIndex < 0 || newIndex < 0) return;
        setSizes((arr) => arrayMove(arr, oldIndex, newIndex));
    };

    const canSave = name.trim() && sizes.length > 0;

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-xl p-0 gap-0 overflow-hidden" data-testid="series-dialog">
                <DialogTitle className="sr-only">
                    {isEdit ? `${state.initial?.name || "Seri"} Düzenleniyor` : "Yeni Beden Serisi"}
                </DialogTitle>
                <div className="px-6 py-5 border-b border-border flex items-start justify-between gap-3">
                    <div>
                        <div className="text-[10px] font-semibold tracking-wider uppercase text-muted-foreground">
                            {isEdit ? "Seri Düzenleniyor" : "Yeni Beden Serisi"}
                        </div>
                        <h2 className="font-display text-lg font-bold tracking-tight mt-0.5">
                            {isEdit ? state.initial.name : "Hangi Setleri Kullanacaksın?"}
                        </h2>
                    </div>
                </div>

                <div className="px-6 py-5 max-h-[480px] overflow-y-auto scroll-thin space-y-4">
                    <Field label="Seri Adı" required>
                        <Input
                            autoFocus={!isEdit}
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="örn. Alfa (XS–XXL)"
                            className="h-10 text-sm"
                            data-testid="series-name-input"
                        />
                    </Field>
                    <Field
                        label="Bedenler"
                        required
                        hint={
                            isEdit
                                ? "Sıralamak için sürükle. Beden ekleme/silme detay panelinden yapılır."
                                : "Enter ile hızlıca ekle. Sıralamak için sürükleyip bırak."
                        }
                    >
                        <div className="space-y-4">
                            {!isEdit && (
                                <div className="flex items-center gap-2">
                                    <Input
                                        ref={sizeRef}
                                        value={sizeInput}
                                        onChange={(e) => setSizeInput(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                e.preventDefault();
                                                addSize();
                                            }
                                        }}
                                        placeholder="XS, S, M, L… veya 34, 36, 38…"
                                        className="h-10 text-sm flex-1"
                                        data-testid="dialog-size-input"
                                    />
                                    <Button
                                        type="button"
                                        onClick={addSize}
                                        variant="outline"
                                        className="h-10 text-xs gap-1.5"
                                        disabled={!sizeInput.trim()}
                                        data-testid="dialog-size-add"
                                    >
                                        <Plus className="h-3 w-3" /> Ekle
                                    </Button>
                                </div>
                            )}
                            {sizes.length > 0 && (
                                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                                    <SortableContext items={sizes} strategy={verticalListSortingStrategy}>
                                        <div className="rounded-lg border border-border overflow-hidden">
                                            {sizes.map((sz, i) => (
                                                <SortableRow
                                                    key={sz}
                                                    id={sz}
                                                    index={i}
                                                    label={sz}
                                                    usageCount={usageBySize.get(sz) || 0}
                                                    onRemove={isEdit ? undefined : () => removeAt(i)}
                                                    onRename={renameAt}
                                                    testIdPrefix="dialog-size"
                                                    labelClassName="font-display font-bold text-sm"
                                                />
                                            ))}
                                        </div>
                                    </SortableContext>
                                </DndContext>
                            )}
                        </div>
                    </Field>
                </div>

                <div className="px-6 py-4 border-t border-border bg-muted/20 flex items-center justify-between gap-3">
                    <div className="text-[11px] text-muted-foreground">
                        {sizes.length} beden eklendi
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={onClose} className="h-9 text-xs">Vazgeç</Button>
                        <Button
                            size="sm"
                            onClick={() => onSubmit({ name, sizes })}
                            disabled={!canSave}
                            className="h-9 text-xs gap-1.5 bg-brand hover:bg-brand/90 text-brand-fg"
                            data-testid="series-dialog-save"
                        >
                            <Check className="h-3 w-3" /> {isEdit ? "Güncelle" : "Seriyi Oluştur"}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

const Field = ({ label, hint, required, children }) => (
    <div className="space-y-1.5">
        <div className="flex items-center justify-between">
            <label className="text-[11px] font-semibold tracking-wider uppercase text-muted-foreground">
                {label}{required && <span className="text-foreground/40 ml-0.5">*</span>}
            </label>
        </div>
        {children}
        {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
);
